import React, { useCallback, useEffect, useRef, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Trash2, Copy, Search, HardDrive } from "lucide-react";

const KIND_ROUTE_MAP = {
  images: "image",
  "floor-plans": "image",
  pdfs: "pdf",
  videos: "video",
  documents: "document",
};

const formatBytes = (n) => {
  if (!n) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0; let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(1)} ${units[i]}`;
};

const AdminMediaLibrary = () => {
  const [searchParams] = useSearchParams();
  // route path decides kind
  const path = window.location.pathname;
  const kindKey = path.split("/").pop();
  const kindFilter = KIND_ROUTE_MAP[kindKey] || null;

  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({});
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [debouncedQ, setDebouncedQ] = useState("");

  useEffect(() => { const t = setTimeout(() => setDebouncedQ(q), 300); return () => clearTimeout(t); }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (kindFilter) params.set("kind", kindFilter);
      if (debouncedQ) params.set("q", debouncedQ);
      params.set("limit", "60");
      const { data } = await api.get(`/admin/media?${params}`);
      setItems(data.items); setTotal(data.total); setStats(data.stats || {});
    } catch (e) { toast.error("Failed to load media", { description: e.response?.data?.detail || "" }); }
    finally { setLoading(false); }
  }, [kindFilter, debouncedQ]);
  useEffect(() => { load(); }, [load]);

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const kind = kindFilter || "image";
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/uploads?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} file(s) uploaded`);
      load();
    } catch (e) { toast.error("Upload failed", { description: e.response?.data?.detail || "" }); }
    finally { setUploading(false); }
  };

  const bulkDelete = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`Delete ${selected.length} file(s) from library? (Storage files may remain.)`)) return;
    try {
      await api.post("/admin/media/bulk-delete", { ids: selected });
      toast.success("Files removed from library");
      setSelected([]); load();
    } catch (e) { toast.error("Delete failed", { description: e.response?.data?.detail || "" }); }
  };

  const copyUrl = (u) => {
    navigator.clipboard.writeText(u).then(() => toast.success("URL copied"));
  };

  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const absoluteUrl = (u) => (u?.startsWith("http") ? u : `${backendUrl}${u}`);

  return (
    <AdminLayout title="Media Library" actions={
      <div className="flex gap-2">
        <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => upload(e.target.files)} data-testid="media-file-input" />
        <Button data-testid="media-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-none bg-accent hover:bg-accent/90">
          <Upload size={14} className="mr-2" /> {uploading ? "Uploading…" : "Upload"}
        </Button>
        {selected.length > 0 && (
          <Button data-testid="media-bulk-delete-btn" variant="outline" onClick={bulkDelete} className="rounded-none text-destructive">
            <Trash2 size={14} className="mr-2" /> Delete {selected.length}
          </Button>
        )}
      </div>
    }>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4" data-testid="media-stats">
        {Object.entries(stats).map(([kind, s]) => (
          <div key={kind} className="border border-border bg-card p-4">
            <div className="overline text-accent">{kind}</div>
            <div className="font-display text-2xl mt-1">{s.count}</div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-widest">{formatBytes(s.size)}</div>
          </div>
        ))}
      </div>
      <div className="border border-border bg-card p-4 mb-4 flex gap-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="media-search" placeholder="Search filename…" value={q} onChange={(e) => setQ(e.target.value)} className="rounded-none h-11 pl-9" />
        </div>
        <div className="text-sm text-muted-foreground flex items-center gap-2"><HardDrive size={14} /> {total} files</div>
      </div>

      {loading && <div className="text-muted-foreground text-sm p-8" data-testid="media-loading">Loading…</div>}
      {!loading && items.length === 0 && <div className="p-12 text-center border border-border text-sm text-muted-foreground" data-testid="media-empty">No files yet. Upload one to get started.</div>}

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {items.map((f) => {
          const isImage = f.kind === "image" || (f.content_type || "").startsWith("image/");
          const isSel = selected.includes(f.id);
          return (
            <div key={f.id} data-testid={`media-tile-${f.id}`} className={`relative border ${isSel ? "border-accent" : "border-border"} bg-card group`}>
              <label className="absolute top-2 left-2 z-10 bg-background/90 border border-border px-1 py-0.5 flex items-center gap-1 text-xs cursor-pointer">
                <input type="checkbox" checked={isSel} onChange={() => setSelected((s) => isSel ? s.filter((x) => x !== f.id) : [...s, f.id])} data-testid={`media-select-${f.id}`} />
              </label>
              <div className="aspect-square bg-background overflow-hidden">
                {isImage ? (
                  <img src={absoluteUrl(f.url)} alt={f.filename} className="w-full h-full object-cover" />
                ) : (
                  <div className="flex items-center justify-center h-full text-xs uppercase text-muted-foreground">{f.kind}</div>
                )}
              </div>
              <div className="p-2 text-xs">
                <div className="truncate" title={f.filename}>{f.filename}</div>
                <div className="text-muted-foreground">{formatBytes(f.size)}</div>
                <button data-testid={`media-copy-${f.id}`} onClick={() => copyUrl(absoluteUrl(f.url))} className="mt-1 flex items-center gap-1 text-accent hover:underline text-[11px]">
                  <Copy size={10} /> Copy URL
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </AdminLayout>
  );
};

export default AdminMediaLibrary;
